import { cuid } from '@obvio/utils'
import {
  useContext,
  useMemo,
  createContext,
  useState,
  useCallback,
} from 'react'

import type { Artwork } from './FormArtwork'
import type { ReactElement } from 'react'

type ContextValue = {
  data: Map<string, Artwork>
  addArtwork(artwork: Artwork): void
  editArtwork(id: string, data: Artwork): void
  removeArtwork(id: string): void
}

const Context = createContext<ContextValue>({
  data: new Map(),
  addArtwork: () => null,
  editArtwork: () => null,
  removeArtwork: () => null,
})

type ArtworksContextProviderProps = {
  children: ReactElement
}

// hook to use the context
export function useArtworksContext(): ContextValue {
  const context = useContext(Context)
  if (context === undefined) {
    throw new Error(
      'useArtworksContext must be used within a ArtworksContextProvider',
    )
  }
  return context
}

export function ArtworksContextProvider({
  children,
}: ArtworksContextProviderProps): ReactElement {
  const [data, setData] = useState<Map<string, Artwork>>(new Map())

  const addArtwork = useCallback((artwork: Artwork) => {
    setData((artworks) => new Map(artworks).set(cuid(), artwork))
  }, [])

  const removeArtwork = useCallback((id: string) => {
    setData((artworks) => {
      const newArtworks = new Map(artworks)
      newArtworks.delete(id)
      return newArtworks
    })
  }, [])

  const editArtwork = useCallback((id: string, newArtwork: Artwork) => {
    setData((artworks) => new Map(artworks).set(id, newArtwork))
  }, [])

  const value = useMemo(
    () => ({ data, addArtwork, removeArtwork, editArtwork }),
    [addArtwork, data, removeArtwork, editArtwork],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}
